import { useMemo, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { formatMedicationTime, loadMedications, saveMedications, todayKey, type MedicationReminder } from '../lib/medications'

function BellIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>
}

function requestNotifications() {
  if (!('Notification' in window)) return
  if (Notification.permission === 'default') void Notification.requestPermission()
}

export function Medication() {
  const [items, setItems] = useState<MedicationReminder[]>(loadMedications)
  const [showAdd, setShowAdd] = useState(false)
  const today = todayKey()
  const sorted = useMemo(() => [...items].sort((a, b) => a.time.localeCompare(b.time)), [items])

  const persist = (next: MedicationReminder[]) => {
    setItems(next)
    saveMedications(next)
  }

  const update = (id: string, changes: Partial<MedicationReminder>) => {
    persist(items.map(item => item.id === id ? { ...item, ...changes } : item))
  }

  const toggleTaken = (item: MedicationReminder) => {
    if (item.takenDate === today) {
      update(item.id, { takenDate: undefined, takenAt: undefined })
      return
    }
    const now = new Date()
    update(item.id, {
      takenDate: today,
      takenAt: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
    })
  }

  const toggleReminder = (item: MedicationReminder) => {
    const next = !item.reminderEnabled
    if (next) requestNotifications()
    update(item.id, { reminderEnabled: next, lastAlertDate: undefined })
  }

  return <div className="min-h-screen bg-[#f5f8fb] pb-28 text-slate-900">
    <TopBar title="Medication" subtitle="My medication list and reminders" />
    <div className="space-y-4 px-3.5 py-4">
      <section className="overflow-hidden rounded-[24px] border border-[#dbe6ef] bg-white shadow-[0_8px_24px_rgba(36,79,132,.07)]">
        <div className="bg-gradient-to-r from-[#173f82] via-[#245da8] to-[#3374bc] px-4 py-4 text-white">
          <p className="text-[10px] font-black uppercase tracking-[.13em] text-blue-100">Today</p>
          <h1 className="mt-1 text-lg font-black">Medication reminders</h1>
          <p className="mt-1 text-xs font-semibold text-blue-100">Tick each item when it has been taken. Turn the bell on for a reminder at the due time.</p>
        </div>
      </section>

      <section className="space-y-3">
        {sorted.map(item => {
          const taken = item.takenDate === today
          return <article key={item.id} className={`rounded-[22px] border bg-white p-4 shadow-[0_5px_18px_rgba(15,23,42,.05)] ${taken ? 'border-[#b9dfd7]' : 'border-slate-200'}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => toggleTaken(item)} aria-label={taken ? 'Mark medication not taken' : 'Mark medication taken'} className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-2 text-sm font-black ${taken ? 'border-[#218b89] bg-[#218b89] text-white' : 'border-slate-300 bg-white text-transparent'}`}>✓</button>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2"><div className="min-w-0"><h2 className="truncate text-sm font-black">{item.name}</h2>{item.dose && <p className="mt-0.5 text-xs font-bold text-slate-500">{item.dose}</p>}</div><button onClick={() => toggleReminder(item)} aria-label={item.reminderEnabled ? 'Turn reminder off' : 'Turn reminder on'} className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${item.reminderEnabled ? 'bg-[#7558b5] text-white' : 'bg-slate-100 text-slate-400'}`}><BellIcon /></button></div>
                {item.instructions && <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">{item.instructions}</p>}
                <div className="mt-3 grid grid-cols-[1fr_auto] items-end gap-3">
                  <label className="text-[10px] font-black uppercase tracking-wide text-slate-400">Due time<input type="time" value={item.time} onChange={e => update(item.id, { time: e.target.value, lastAlertDate: undefined })} className="input mt-1 w-full" /></label>
                  <div className="pb-2 text-right"><p className="text-[10px] font-black uppercase tracking-wide text-slate-400">Reminder</p><p className="mt-1 text-xs font-black text-[#7558b5]">{formatMedicationTime(item.time)}</p></div>
                </div>
                {taken && <div className="mt-3 rounded-xl bg-[#edf9f6] px-3 py-2 text-xs font-black text-[#177f80]">Taken ✓ {item.takenAt ? `at ${formatMedicationTime(item.takenAt)}` : ''}</div>}
              </div>
            </div>
          </article>
        })}
      </section>

      <button onClick={() => setShowAdd(value => !value)} className="w-full rounded-2xl bg-[#245a9d] py-3 text-sm font-black text-white">{showAdd ? 'Cancel' : '+ Add medication'}</button>

      {showAdd && <AddMedication onSave={item => { persist([...items, item]); setShowAdd(false) }} />}

      <section className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_5px_18px_rgba(15,23,42,.05)]">
        <p className="text-xs font-black text-slate-700">About reminders</p>
        <p className="mt-1 text-[11px] font-semibold leading-5 text-slate-500">When the bell is on, Marnixia plays a bell and sends a browser notification at the due time while the app is running. The browser may ask permission the first time notifications are enabled.</p>
      </section>
    </div>
  </div>
}

function AddMedication({ onSave }: { onSave: (item: MedicationReminder) => void }) {
  const [name, setName] = useState('')
  const [dose, setDose] = useState('')
  const [instructions, setInstructions] = useState('')
  const [time, setTime] = useState('08:00')
  const [reminderEnabled, setReminderEnabled] = useState(true)

  const save = () => {
    if (!name.trim()) return
    if (reminderEnabled) requestNotifications()
    onSave({ id: `med-${Date.now()}`, name: name.trim(), dose: dose.trim() || undefined, instructions: instructions.trim() || undefined, time, reminderEnabled })
  }

  return <section className="rounded-[22px] border border-[#dbe6ef] bg-white p-4 shadow-[0_5px_18px_rgba(15,23,42,.05)]">
    <p className="text-sm font-black">Add medication</p>
    <div className="mt-3 space-y-3">
      <label className="block text-xs font-bold text-slate-600">Medication name<input value={name} onChange={e => setName(e.target.value)} className="input mt-1" placeholder="Medication name" /></label>
      <label className="block text-xs font-bold text-slate-600">Dose<input value={dose} onChange={e => setDose(e.target.value)} className="input mt-1" placeholder="e.g. 1 tablet" /></label>
      <label className="block text-xs font-bold text-slate-600">Instructions<input value={instructions} onChange={e => setInstructions(e.target.value)} className="input mt-1" placeholder="e.g. Take with food" /></label>
      <label className="block text-xs font-bold text-slate-600">Time<input type="time" value={time} onChange={e => setTime(e.target.value)} className="input mt-1" /></label>
      <button onClick={() => setReminderEnabled(value => !value)} className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-left ${reminderEnabled ? 'bg-[#f0ecfb] text-[#7558b5]' : 'bg-slate-100 text-slate-500'}`}><span><span className="block text-xs font-black">Bell reminder</span><span className="mt-0.5 block text-[10px] font-semibold">Sound + notification at due time</span></span><span className={`flex h-9 w-9 items-center justify-center rounded-xl ${reminderEnabled ? 'bg-[#7558b5] text-white' : 'bg-white text-slate-400'}`}><BellIcon /></span></button>
      <button onClick={save} className="w-full rounded-xl bg-[#218b89] py-3 text-sm font-black text-white">Save medication</button>
    </div>
  </section>
}

